/*
 * Splash chat-history store (v0 — localStorage).
 *
 * Persists past chat sessions in the browser so the user can browse
 * and resume them via the clock-icon popover in the Splash banner.
 *
 * STORAGE SHAPE:
 *   localStorage key  `aquastar.splash.chats.v1.<userhash>`  →  ChatSession[]
 *   localStorage key  `aquastar.splash.activeChat.v1.<userhash>` → string
 *
 * Per-user scoping: the suffix is a tiny non-cryptographic hash of the
 * user's email. Goal isn't security (localStorage is sandboxed per
 * origin already) — it's privacy across the rare case of two users
 * sharing a browser. A user signing in to a different account gets a
 * different suffix and won't see the prior user's chats. The hash isn't
 * the email itself so casual inspection of localStorage doesn't leak
 * the address either.
 *
 * Capped at MAX_CHATS sessions per user; LRU eviction by lastTurnAt
 * when exceeded.
 *
 * MESSAGES SCHEMA: we store user/assistant turns ONLY — the system
 * prompt is regenerated fresh each turn by buildSystemPrompt() in
 * SplashAgent. Storing the rendered system prompt would freeze old
 * chats with stale rules; re-prepending the live one means a chat
 * resumed weeks later still benefits from prompt updates.
 *
 * FUTURE v1 — server-side migration:
 *   New per-app table app_aquastar_dev.splash_chat_sessions
 *     (id UUID, user_id UUID, started_at, last_turn_at,
 *      title NVARCHAR(120), messages_json NVARCHAR(MAX))
 *   Two new handlers:
 *     - aquastarChatList(userId)  → recent N sessions (id/title/timestamps)
 *     - aquastarChatGet(id)        → full messages_json
 *   SPA syncs on chat boundary + every Nth turn. localStorage stays
 *   as a fast-paint cache.
 */

const STORAGE_PREFIX = 'aquastar.splash.chats.v1';
const ACTIVE_PREFIX = 'aquastar.splash.activeChat.v1';
const MAX_CHATS = 50;
const TITLE_MAX = 60;

export interface StoredMessage {
  role: 'user' | 'assistant';
  content: string;
  // We deliberately drop transient runtime fields (pendingActions,
  // actionDecisions, error, hidden) — those are mid-flight UI state,
  // not part of the conversation worth persisting.
}

export interface ChatSession {
  id: string;
  createdAt: string; // ISO
  lastTurnAt: string; // ISO
  title: string;
  messages: StoredMessage[];
}

/**
 * Tiny stable hash → base36 string. Not cryptographic; purpose is
 * "different users get different suffixes" without storing the email
 * verbatim. djb2-ish.
 */
function hashScope(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

function chatsKey(userEmail: string | null | undefined): string {
  if (!userEmail) return `${STORAGE_PREFIX}.guest`;
  return `${STORAGE_PREFIX}.${hashScope(userEmail.toLowerCase())}`;
}

function activeKey(userEmail: string | null | undefined): string {
  if (!userEmail) return `${ACTIVE_PREFIX}.guest`;
  return `${ACTIVE_PREFIX}.${hashScope(userEmail.toLowerCase())}`;
}

/** Defensive: localStorage can throw (private mode, quota). */
function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeWrite(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}
function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readChats(userEmail: string | null | undefined): ChatSession[] {
  const raw = safeRead(chatsKey(userEmail));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Best-effort validation. Drop anything unrecognisable.
    return parsed.filter(
      (c): c is ChatSession =>
        c &&
        typeof c.id === 'string' &&
        typeof c.createdAt === 'string' &&
        typeof c.lastTurnAt === 'string' &&
        Array.isArray(c.messages),
    );
  } catch {
    return [];
  }
}

function writeChats(
  userEmail: string | null | undefined,
  chats: ChatSession[],
): void {
  // Cap + LRU evict by lastTurnAt.
  const sorted = [...chats].sort((a, b) =>
    b.lastTurnAt.localeCompare(a.lastTurnAt),
  );
  const capped = sorted.slice(0, MAX_CHATS);
  safeWrite(chatsKey(userEmail), JSON.stringify(capped));
}

/**
 * Auto-derive a chat title from its first user message. Trimmed to
 * TITLE_MAX chars on a word boundary when possible.
 */
function deriveTitle(messages: StoredMessage[]): string {
  const firstUser = messages.find((m) => m.role === 'user');
  if (!firstUser) return 'New chat';
  const cleaned = firstUser.content
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length <= TITLE_MAX) return cleaned || 'New chat';
  const slice = cleaned.slice(0, TITLE_MAX);
  const lastSpace = slice.lastIndexOf(' ');
  return (lastSpace > 30 ? slice.slice(0, lastSpace) : slice) + '…';
}

export function newChatId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `chat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function listChats(userEmail: string | null | undefined): ChatSession[] {
  return readChats(userEmail).sort((a, b) =>
    b.lastTurnAt.localeCompare(a.lastTurnAt),
  );
}

export function getChat(
  id: string,
  userEmail: string | null | undefined,
): ChatSession | null {
  return readChats(userEmail).find((c) => c.id === id) ?? null;
}

/**
 * Upsert a chat. Auto-derives title from first user message if not
 * already set, refreshes lastTurnAt to now. Empty chats (no messages
 * at all OR no user message) are NOT persisted — that avoids polluting
 * the list with one-click "I opened then closed Splash" noise.
 */
export function saveChat(
  chat: Omit<ChatSession, 'lastTurnAt' | 'title'> & {
    lastTurnAt?: string;
    title?: string;
  },
  userEmail: string | null | undefined,
): ChatSession | null {
  if (!chat.messages || chat.messages.length === 0) return null;
  if (!chat.messages.some((m) => m.role === 'user')) return null;
  const lastTurnAt = chat.lastTurnAt ?? new Date().toISOString();
  const title = chat.title ?? deriveTitle(chat.messages);
  const updated: ChatSession = {
    id: chat.id,
    createdAt: chat.createdAt,
    lastTurnAt,
    title,
    messages: chat.messages,
  };
  const all = readChats(userEmail);
  const idx = all.findIndex((c) => c.id === chat.id);
  if (idx >= 0) all[idx] = updated;
  else all.push(updated);
  writeChats(userEmail, all);
  return updated;
}

export function deleteChat(
  id: string,
  userEmail: string | null | undefined,
): void {
  const all = readChats(userEmail).filter((c) => c.id !== id);
  writeChats(userEmail, all);
  if (getActiveChatId(userEmail) === id) setActiveChatId(null, userEmail);
}

export function getActiveChatId(
  userEmail: string | null | undefined,
): string | null {
  return safeRead(activeKey(userEmail));
}

export function setActiveChatId(
  id: string | null,
  userEmail: string | null | undefined,
): void {
  if (id) safeWrite(activeKey(userEmail), id);
  else safeRemove(activeKey(userEmail));
}

/**
 * Format a timestamp as a relative string for the popover row
 * ("2h ago", "Yesterday", "Apr 28"). Uses the user's local clock.
 */
export function formatRelativeChatTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - t) / 1000));
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const d = new Date(iso);
  const today = new Date();
  const isYesterday =
    d.getDate() === today.getDate() - 1 &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear();
  if (isYesterday) return 'Yesterday';
  const sameYear = d.getFullYear() === today.getFullYear();
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(d);
}
